import { AuthenticatedRoutes, UnauthenticatedRoutes } from "routes";
import { FullPageLoading, MaintenancePage } from "pages";
import { useSetupAuth0, useToast } from "hooks";

import { Box } from "@chakra-ui/react";
import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { usePermissions } from "contexts";
import LogRocket from "logrocket";
import setupLogRocketReact from "logrocket-react";
import { useOpenReplayIdentity } from "shared/openreplay/useOpenReplayIdentity";

const IS_PROD_SITE = ["galveston", "longview", "tyler"].some((city) =>
  window.location.hostname.includes(city)
);

if (IS_PROD_SITE) {
  LogRocket.init("8f4znn/emr");
  setupLogRocketReact(LogRocket);
}

type Settings = {
  allowed: boolean;
  mode: "maintenance" | "normal";
} | null;

function UnauthenticatedApp() {
  return (
    <Box height="100vh">
      <UnauthenticatedRoutes />
    </Box>
  );
}

function AuthenticatedApp() {
  const authReady = useSetupAuth0();
  const { isLoadingPermissions, claims } = usePermissions();

  const settings = claims?.settings as Settings;

  if (!authReady || isLoadingPermissions) {
    return <FullPageLoading />;
  }

  return (
    <Box height="100vh">
      <AuthenticatedRoutes />
      {settings?.mode === "maintenance" && !settings?.allowed && (
        <MaintenancePage />
      )}
    </Box>
  );
}

export const App = () => {
  const { isLoading, error, isAuthenticated, user } = useAuth0();
  const toast = useToast();

  React.useEffect(() => {
    if (user && IS_PROD_SITE) {
      LogRocket.identify(user?.sub || "", {
        name: user?.name || "",
        email: user?.email || "",
        picture: user?.picture || "",
        sub: user?.sub || "",
      });
    }
  }, [user]);

  useOpenReplayIdentity();

  React.useEffect(() => {
    if (error) {
      toast({ description: error?.message });
    }
  }, [error, toast]);

  if (isLoading) {
    return <FullPageLoading />;
  }

  if (isAuthenticated) {
    return <AuthenticatedApp />;
  }

  return <UnauthenticatedApp />;
};
